import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { CatalogItemCard } from '../components/CatalogItemCard';
import { CustomSelect } from '../components/CustomSelect';
import type { CatalogItem, PlayerInventoryResponse, PlayerProfileResponse } from '../types';

const LIMIT = 24;

const SORT_OPTIONS = [
  { value: 'quantity_desc', label: 'Most first' },
  { value: 'quantity_asc',  label: 'Least first' },
  { value: 'name_asc',      label: 'Name A–Z' },
  { value: 'name_desc',     label: 'Name Z–A' },
  { value: 'rarity_desc',   label: 'Rarity (high→low)' },
  { value: 'rarity_asc',    label: 'Rarity (low→high)' },
];

export function PlayerPage() {
  const { robloxId = '' } = useParams();

  const [data, setData]         = useState<PlayerProfileResponse | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError]     = useState<string | null>(null);

  const [inv, setInv]           = useState<PlayerInventoryResponse | null>(null);
  const [invLoading, setInvLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [type,   setType]   = useState('All');
  const [rarity, setRarity] = useState('All');
  const [sort,   setSort]   = useState('quantity_desc');
  const [page,   setPage]   = useState(1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setProfileLoading(true);
        const result = await api.getPlayer(robloxId);
        if (!cancelled) { setData(result); setProfileError(null); }
      } catch (err) {
        if (!cancelled) setProfileError(err instanceof Error ? err.message : 'Failed to load player');
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [robloxId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setInvLoading(true);
        const result = await api.getPlayerInventory(robloxId, { search, type, rarity, sort, page, limit: LIMIT });
        if (!cancelled) setInv(result);
      } catch {
      } finally {
        if (!cancelled) setInvLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [robloxId, search, type, rarity, sort, page]);

  const inventoryCards: CatalogItem[] = (inv?.results ?? []).map((item, index) => ({
    id: index,
    item_key: item.item_key,
    name: item.name,
    category: item.category,
    type: item.type,
    rarity: item.rarity,
    image_url: item.image_url,
    owners: 0,
    copies: item.quantity,
  }));

  const totalPages = inv ? Math.max(1, Math.ceil(inv.total / LIMIT)) : 1;

  if (profileLoading) return <div className="panel empty-state">Loading player profile...</div>;
  if (profileError || !data) return <div className="panel empty-state">{profileError || 'Player not found'}</div>;

  return (
    <div className="page-stack">
      <div className="detail-breadcrumb">
        <Link to="/rankings" className="text-link">Rankings</Link>
        <span>/</span>
        <span>{data.user.display_name || data.user.username}</span>
      </div>

      <section className="hero-panel hero-panel--compact">
        <div className="player-hero">
          {data.user.headshot_url
            ? <img className="player-avatar" src={data.user.headshot_url} alt={data.user.username} />
            : <div className="player-avatar player-avatar--placeholder" />}
          <div>
            <p className="eyebrow">Collector profile</p>
            <h1>{data.user.display_name || data.user.username}</h1>
            <p className="hero-copy">@{data.user.username} · Roblox ID {data.user.roblox_id.toLocaleString()}</p>
            <a
              className="button button--roblox"
              href={`https://www.roblox.com/users/${data.user.roblox_id}/profile`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Roblox ↗
            </a>
          </div>
        </div>
        <div className="hero-stats">
          <Stat label="Total"       value={data.stats?.total_items  || 0} />
          <Stat label="Unique"      value={data.stats?.unique_items || 0} />
          <Stat label="Mythicals"   value={data.stats?.mythicals    || 0} />
          <Stat label="Legendaries" value={data.stats?.legendaries  || 0} />
        </div>
      </section>

      <section className="panel page-stack">
        <div className="panel__header">
          <div>
            <p className="eyebrow">Inventory</p>
            <h2>Known FTF holdings</h2>
          </div>
        </div>

        {/* Filter bar */}
        <div className="filter-row">
          <input
            className="search-input"
            type="search"
            placeholder="Search by item name"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
          <CustomSelect
            value={type}
            options={['All', 'Gem', 'Hammer'].map(v => ({ value: v, label: v === 'All' ? 'All types' : v }))}
            onChange={(v) => { setType(v); setPage(1); }}
          />
          <CustomSelect
            value={rarity}
            options={['All', 'Mythical', 'Legendary', 'Rare', 'Uncommon', 'Common', 'Default'].map(v => ({ value: v, label: v === 'All' ? 'All rarities' : v }))}
            onChange={(v) => { setRarity(v); setPage(1); }}
          />
          <CustomSelect
            value={sort}
            options={SORT_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
            onChange={(v) => { setSort(v); setPage(1); }}
          />
        </div>

        {inv && (
          <p className="catalog-summary">
            {inv.total.toLocaleString()} item{inv.total !== 1 ? 's' : ''}
            {(search || type !== 'All' || rarity !== 'All') && ' matching filters'}
          </p>
        )}

        {invLoading
          ? <div className="empty-state">Loading inventory…</div>
          : (
            <>
              <div className="item-grid">
                {inventoryCards.map((item) => <CatalogItemCard key={item.item_key} item={item} />)}
              </div>
              {inventoryCards.length === 0 && (
                <div className="empty-state">No items match these filters.</div>
              )}
            </>
          )
        }

        {totalPages > 1 && (
          <div className="pager">
            <button
              className="pager__btn"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >← Previous</button>
            <span className="pager__info">Page {page} of {totalPages}</span>
            <button
              className="pager__btn"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >Next →</button>
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="stat-card">
      <span>{label}</span>
      <strong>{value.toLocaleString()}</strong>
    </div>
  );
}
